import React from 'react';
import {motion} from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import {useAuth0Data} from '../../../context/Auth0DataContext';
import {format, parseISO} from 'date-fns';

const {FiBarChart3, FiPlus, FiActivity, FiTrendingUp, FiCalendar} = FiIcons;

const PerformanceGraphWidget = () => {
  const {data} = useAuth0Data();
  const {revenue, grossMargin, historicalRevenueGM} = data;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate TOTAL values from ALL data points
  const calculateTotals = () => {
    if (!historicalRevenueGM || historicalRevenueGM.length === 0) {
      return {totalRevenue: 0, totalGrossMargin: 0};
    }
    
    const totalRevenue = historicalRevenueGM.reduce((sum, point) => sum + (point.revenue || 0), 0);
    const totalGrossMargin = historicalRevenueGM.reduce((sum, point) => sum + (point.grossMargin || 0), 0);
    
    return {totalRevenue, totalGrossMargin};
  };

  // Get latest data point for date reference
  const getLatestDataPoint = () => {
    if (!historicalRevenueGM || historicalRevenueGM.length === 0) return null;
    const validData = historicalRevenueGM.filter(point => point.date); // Filter out null dates
    if (validData.length === 0) return null;
    const sortedData = [...validData].sort((a, b) => new Date(b.date) - new Date(a.date));
    return sortedData[0];
  };

  const latestDataPoint = getLatestDataPoint();
  const {totalRevenue, totalGrossMargin} = calculateTotals();

  if (revenue.goal === 0 && grossMargin.goal === 0) {
    return (
      <motion.div
        initial={{opacity: 0, y: 20}}
        animate={{opacity: 1, y: 0}}
        className="relative overflow-hidden glass rounded-2xl border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5"></div>
        <div className="relative p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <motion.div
                className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg"
                whileHover={{rotate: 360, scale: 1.1}}
                transition={{duration: 0.6}}
              >
                <SafeIcon icon={FiBarChart3} className="w-6 h-6 text-white" />
              </motion.div>
              <div className="ml-4">
                <h3 className="text-xl font-bold text-gray-900">Performance Overview</h3>
                <p className="text-gray-500">Track your business metrics</p>
              </div>
            </div>
          </div>

          <div className="text-center py-16">
            <motion.div
              initial={{scale: 0}}
              animate={{scale: 1}}
              transition={{delay: 0.2, type: "spring"}}
              className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-6"
            >
              <SafeIcon icon={FiPlus} className="w-12 h-12 text-gray-400" />
            </motion.div>
            <h4 className="text-lg font-semibold text-gray-700 mb-2">No Performance Data</h4>
            <p className="text-gray-500 mb-6">Set up revenue and gross margin goals to view performance analytics</p>
            <motion.button
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.95}}
              className="btn-primary px-6 py-3 rounded-xl"
            >
              Configure Goals
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  // Prepare data for chart - show cumulative totals over time
  const prepareChartData = () => {
    if (!historicalRevenueGM || historicalRevenueGM.length === 0) {
      return {
        dates: [],
        revenueData: [],
        grossMarginData: [],
      };
    }

    // Sort historical data by date
    const sortedData = [...historicalRevenueGM]
      .filter(point => point.date) // Filter out entries with null/undefined dates
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    const dates = sortedData.map(point => {
      try {
        // Handle both ISO strings and Date objects
        const dateObj = typeof point.date === 'string' ? parseISO(point.date) : new Date(point.date);
        return format(dateObj, 'MMM yyyy');
      } catch (error) {
        console.error('Error formatting date:', point.date, error);
        return 'Invalid Date';
      }
    });
    
    // Calculate running totals (cumulative sum)
    let revenueRunningTotal = 0;
    let gmRunningTotal = 0;
    
    const revenueData = sortedData.map(point => {
      revenueRunningTotal += (point.revenue || 0);
      return revenueRunningTotal;
    });
    
    const grossMarginData = sortedData.map(point => {
      gmRunningTotal += (point.grossMargin || 0);
      return gmRunningTotal;
    });

    return {
      dates,
      revenueData,
      grossMarginData,
    };
  };

  const {dates, revenueData, grossMarginData} = prepareChartData();

  const option = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: 'rgba(0, 0, 0, 0.1)',
      borderWidth: 1,
      borderRadius: 12,
      textStyle: {
        color: '#374151',
        fontSize: 14,
      },
      formatter: function(params) {
        let result = params[0].name + '<br/>';
        params.forEach(param => {
          if (param.value > 0) {
            result += `<span style="color: ${param.color}">●</span> ${param.seriesName}: ${formatCurrency(param.value)}<br/>`;
          }
        });
        return result;
      }
    },
    legend: {
      data: ['Cumulative Revenue', 'Cumulative Gross Margin', 'Revenue Goal', 'GM Goal'],
      top: 20,
      textStyle: {
        color: '#6b7280',
        fontSize: 14,
        fontWeight: 500,
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '10%',
      top: '15%',
      containLabel: true
    },
    xAxis: [{
      type: 'category',
      boundaryGap: false,
      data: dates,
      axisLine: {
        lineStyle: {
          color: '#e5e7eb'
        }
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        color: '#9ca3af',
        fontSize: 12,
        fontWeight: 500,
      }
    }],
    yAxis: [{
      type: 'value',
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      splitLine: {
        lineStyle: {
          color: '#f3f4f6',
          type: 'dashed'
        }
      },
      axisLabel: {
        color: '#9ca3af',
        fontSize: 12,
        fontWeight: 500,
        formatter: function(value) {
          return formatCurrency(value);
        }
      }
    }],
    series: [
      {
        name: 'Cumulative Revenue',
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 8,
        data: revenueData,
        lineStyle: {
          color: '#3b82f6',
          width: 4,
          shadowColor: 'rgba(59, 130, 246, 0.3)',
          shadowBlur: 10,
        },
        itemStyle: {
          color: '#3b82f6',
          borderWidth: 2,
          borderColor: '#ffffff',
          shadowColor: 'rgba(59, 130, 246, 0.5)',
          shadowBlur: 8,
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {offset: 0, color: 'rgba(59, 130, 246, 0.3)'},
              {offset: 1, color: 'rgba(59, 130, 246, 0.05)'}
            ]
          }
        }
      },
      {
        name: 'Cumulative Gross Margin',
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: 8,
        data: grossMarginData,
        lineStyle: {
          color: '#10b981',
          width: 4,
          shadowColor: 'rgba(16, 185, 129, 0.3)',
          shadowBlur: 10,
        },
        itemStyle: {
          color: '#10b981',
          borderWidth: 2,
          borderColor: '#ffffff',
          shadowColor: 'rgba(16, 185, 129, 0.5)',
          shadowBlur: 8,
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {offset: 0, color: 'rgba(16, 185, 129, 0.3)'},
              {offset: 1, color: 'rgba(16, 185, 129, 0.05)'}
            ]
          }
        }
      },
      ...(revenue.goal > 0 ? [{
        name: 'Revenue Goal',
        type: 'line',
        lineStyle: {
          color: '#6b7280',
          type: 'dashed',
          width: 2
        },
        itemStyle: {
          color: '#6b7280'
        },
        symbol: 'none',
        data: Array(dates.length).fill(revenue.goal)
      }] : []),
      ...(grossMargin.goal > 0 ? [{
        name: 'GM Goal',
        type: 'line',
        lineStyle: {
          color: '#9ca3af',
          type: 'dashed',
          width: 2
        },
        itemStyle: {
          color: '#9ca3af'
        },
        symbol: 'none',
        data: Array(dates.length).fill(grossMargin.goal)
      }] : [])
    ]
  };

  const gapToGoal = revenue.goal - totalRevenue;
  const gmGapToGoal = grossMargin.goal - totalGrossMargin;

  return (
    <motion.div
      initial={{opacity: 0, y: 20}}
      animate={{opacity: 1, y: 0}}
      className="relative overflow-hidden glass rounded-2xl border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-indigo-500/5 to-purple-500/5"></div>
      <div className="relative p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <motion.div
              className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg"
              whileHover={{rotate: 360, scale: 1.1}}
              transition={{duration: 0.6}}
            >
              <SafeIcon icon={FiBarChart3} className="w-6 h-6 text-white" />
            </motion.div>
            <div className="ml-4">
              <h3 className="text-xl font-bold text-gray-900">Performance Overview</h3>
              <p className="text-gray-500">
                {latestDataPoint 
                  ? `Cumulative totals from ${historicalRevenueGM.length} data points`
                  : 'Revenue and margin trends'
                }
              </p>
            </div>
          </div>
          <motion.div
            className="text-right"
            initial={{opacity: 0, x: 20}}
            animate={{opacity: 1, x: 0}}
            transition={{delay: 0.2}}
          >
            <p className="text-sm text-gray-500 flex items-center justify-end">
              <SafeIcon icon={FiTrendingUp} className="w-4 h-4 mr-2" />
              Gap to Goals
            </p>
            <div className="space-y-1">
              <p className="text-sm font-bold text-red-600">Revenue: {formatCurrency(Math.max(0, gapToGoal))}</p>
              <p className="text-sm font-bold text-red-600">GM: {formatCurrency(Math.max(0, gmGapToGoal))}</p>
            </div>
          </motion.div>
        </div>

        {/* Chart */}
        <motion.div
          style={{height: '400px'}}
          initial={{opacity: 0, scale: 0.95}}
          animate={{opacity: 1, scale: 1}}
          transition={{delay: 0.3}}
        >
          <ReactECharts
            option={option}
            style={{height: '100%', width: '100%'}}
            opts={{renderer: 'canvas'}}
          />
        </motion.div>

        {/* Stats - Now showing TOTALS */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-white/20"
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          transition={{delay: 0.5}}
        >
          <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
            <SafeIcon icon={FiActivity} className="w-6 h-6 text-blue-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
            <p className="text-lg font-bold text-blue-600">{formatCurrency(totalRevenue)}</p>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl">
            <SafeIcon icon={FiActivity} className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600 mb-1">Total GM</p>
            <p className="text-lg font-bold text-emerald-600">{formatCurrency(totalGrossMargin)}</p>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
            <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-purple-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600 mb-1">Revenue Progress</p>
            <p className="text-lg font-bold text-purple-600">
              {revenue.goal > 0 ? ((totalRevenue / revenue.goal) * 100).toFixed(1) : 0}%
            </p>
          </div>
          <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl">
            <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-orange-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600 mb-1">GM Progress</p>
            <p className="text-lg font-bold text-orange-600">
              {grossMargin.goal > 0 ? ((totalGrossMargin / grossMargin.goal) * 100).toFixed(1) : 0}%
            </p>
          </div>
        </motion.div>

        {/* Data Points Summary */}
        {historicalRevenueGM && historicalRevenueGM.length > 0 && (
          <motion.div
            className="mt-6 pt-6 border-t border-white/20"
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{delay: 0.6}}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-500 mr-2" />
                <span className="text-sm text-gray-600">
                  {historicalRevenueGM.length} data points tracked (showing cumulative totals)
                </span>
              </div>
              {latestDataPoint && latestDataPoint.date && (
                <span className="text-sm text-gray-500">
                  Latest: {format(typeof latestDataPoint.date === 'string' ? parseISO(latestDataPoint.date) : new Date(latestDataPoint.date), 'MMM dd, yyyy')}
                </span>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default PerformanceGraphWidget;